import React, { useEffect, useState } from 'react';
import { AlertTriangle, ClipboardCheck, Flag, Tags, Banknote, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { oapi, fmtMoney } from '@/lib/officialsApi';
import { PayFilters } from './PayFilters';
import { MetricCard, Money, Panel, Loading } from './PayBits';

const Alert = ({ tone, icon: Icon, children, action, testId }) => (
  <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-2.5 text-sm ${tone}`} data-testid={testId}>
    <Icon className="w-4 h-4 shrink-0" /><div className="flex-1 min-w-[200px]">{children}</div>{action}
  </div>
);

const Alerts = ({ d, go, currency }) => {
  const t = d.totals;
  const budgetText = { over: 'is over budget', expected_over: 'is expected to go over budget', near: 'is close to its budget' };
  return (
    <div className="space-y-2">
      {t.pending_verification > 0 && <Alert tone="bg-amber-50 border-amber-200 text-amber-900" icon={ClipboardCheck} testId="alert-verify"
        action={<Button size="sm" variant="outline" onClick={() => go('verify')} data-testid="alert-verify-btn">Verify now</Button>}>
        <b>{t.pending_verification}</b> played assignment{t.pending_verification === 1 ? '' : 's'} waiting for verification</Alert>}
      {t.earned > 0 && <Alert tone="bg-sky-50 border-sky-200 text-sky-900" icon={Eye} testId="alert-approve"
        action={<Button size="sm" variant="outline" onClick={() => go('ledger', { status: 'earned' })} data-testid="alert-approve-btn">Review & approve</Button>}>
        <b>{fmtMoney(t.earned, currency)}</b> verified and waiting for approval</Alert>}
      {t.needs_review > 0 && <Alert tone="bg-red-50 border-red-200 text-red-900" icon={Flag} testId="alert-review"
        action={<Button size="sm" variant="outline" onClick={() => go('ledger', { status: 'needs_review' })} data-testid="alert-review-btn">Review</Button>}>
        <b>{t.needs_review}</b> approved item{t.needs_review === 1 ? '' : 's'} changed after approval</Alert>}
      {t.no_rate > 0 && <Alert tone="bg-slate-50 border-slate-200 text-slate-700" icon={Tags} testId="alert-no-rate"
        action={<Button size="sm" variant="outline" onClick={() => go('rates')} data-testid="alert-rates-btn">Set rates</Button>}>
        <b>{t.no_rate}</b> assignment{t.no_rate === 1 ? '' : 's'} have no rate set (fee $0)</Alert>}
      {(d.budget_alerts || []).map(b => (
        <Alert key={b.event_id} tone="bg-red-50 border-red-200 text-red-900" icon={AlertTriangle} testId={`alert-budget-${b.event_id}`}>
          <b>{b.league}</b> {budgetText[b.warning]}: expected {fmtMoney(b.expected, currency)} of {fmtMoney(b.budget, currency)} budget
        </Alert>
      ))}
    </div>
  );
};

const OwedTable = ({ rows, currency, perms, openOfficial, recordPayment }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="owed-table">
      <thead><tr className="text-[11px] uppercase tracking-wide text-slate-500 bg-slate-50 text-left">
        <th className="px-4 py-2">Official</th><th className="px-2 py-2 text-right">Games</th><th className="px-2 py-2 text-right">Earned</th>
        <th className="px-2 py-2 text-right">Approved</th><th className="px-2 py-2 text-right">Paid</th><th className="px-2 py-2 text-right">Outstanding</th><th className="px-4 py-2" />
      </tr></thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.official_id} className="border-t" data-testid={`owed-row-${r.official_id}`}>
            <td className="px-4 py-2.5"><button type="button" onClick={() => openOfficial(r.official_id)} className="font-semibold text-[#00247D] hover:underline text-left" data-testid={`owed-open-${r.official_id}`}>{r.official_name || 'Unknown'}</button></td>
            <td className="px-2 text-right tabular-nums">{r.games}</td>
            <td className="px-2 text-right"><Money minor={r.earned} currency={currency} /></td>
            <td className="px-2 text-right"><Money minor={r.approved} currency={currency} /></td>
            <td className="px-2 text-right"><Money minor={r.paid} currency={currency} /></td>
            <td className="px-2 text-right font-bold"><Money minor={r.outstanding} currency={currency} testId={`owed-outstanding-${r.official_id}`} /></td>
            <td className="px-4 text-right whitespace-nowrap">
              {perms.has('record') && r.outstanding > 0 && (
                <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => recordPayment({ official_id: r.official_id, official_name: r.official_name })} data-testid={`owed-pay-${r.official_id}`}>
                  <Banknote className="w-4 h-4 mr-1" />Pay
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {!rows.length && <p className="py-10 text-center text-sm text-slate-500">Nobody here yet.</p>}
  </div>
);

export const PayOverview = ({ orgId, currency, perms, competitions, officials, roles, tick, go, openOfficial, recordPayment }) => {
  const [f, setF] = useState({});
  const [d, setD] = useState(null);
  const [all, setAll] = useState(false);
  useEffect(() => {
    oapi('/officials/payments/ledger', { orgId, query: { ...f, include_lines: 0 } }).then(setD).catch(e => toast.error(e.message));
  }, [orgId, f, tick]);
  if (!d) return <Loading />;
  const t = d.totals;
  const rows = all ? d.officials : d.officials.filter(r => r.outstanding > 0 || r.earned > 0);
  return (
    <div className="space-y-4" data-testid="pay-overview">
      <PayFilters f={f} setF={setF} competitions={competitions} officials={officials} roles={roles} showStatus={false} />
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <MetricCard label="Scheduled fees" minor={t.scheduled} currency={currency} tone="text-slate-700" sub="Not yet verified" testId="metric-scheduled" />
        <MetricCard label="Earned" minor={t.earned} currency={currency} tone="text-sky-700" sub="Verified · needs approval" testId="metric-earned" onClick={() => go('ledger', { status: 'earned' })} />
        <MetricCard label="Approved" minor={t.approved} currency={currency} tone="text-indigo-700" sub="Includes paid" testId="metric-approved" />
        <MetricCard label="Paid" minor={t.paid} currency={currency} tone="text-emerald-700" testId="metric-paid" onClick={() => go('payments')} />
        <MetricCard label="Outstanding" minor={t.outstanding} currency={currency} tone="text-red-600" sub="Approved − paid" testId="metric-outstanding" onClick={() => go('ledger', { status: 'outstanding' })} />
        <MetricCard label="Officials awaiting payment" count={t.awaiting_officials} tone="text-[#00247D]" testId="metric-awaiting" />
      </div>
      <Alerts d={d} go={go} currency={currency} />
      <Panel title="Who needs to be paid" desc="Officials with approved balances or verified fees waiting for approval." testId="owed-panel"
        actions={<button type="button" onClick={() => setAll(a => !a)} className="text-xs font-semibold text-[#00247D] hover:underline" data-testid="owed-toggle-all">{all ? 'Only show who is owed' : 'Show all officials'}</button>}>
        <OwedTable rows={rows} currency={currency} perms={perms} openOfficial={openOfficial} recordPayment={recordPayment} />
      </Panel>
    </div>
  );
};
